import { getAccessToken } from '~/utils/authToken'
import { askServerNow, isGatewayDown, isUnanswered, markServerDown, onServerDownChange, whenServerUp } from '~/utils/serverReachability'
import { meansSessionExpired, sessionExpired } from '~/utils/sessionExpired'
import { apiBaseFrom } from '../../shared/apiBase'

export default defineNuxtPlugin(() => {
  const patchedGlobal = globalThis as typeof globalThis & {
    __dockAuthFetchPatched__?: boolean
    __dockAuthOriginalFetch__?: typeof globalThis.fetch
  }

  if (patchedGlobal.__dockAuthFetchPatched__) {
    return
  }

  const runtimeConfig = useRuntimeConfig()
  const apiBase = apiBaseFrom(runtimeConfig.public.apiBase)
  const originalFetch = (patchedGlobal.__dockAuthOriginalFetch__ ?? globalThis.fetch.bind(globalThis))
  patchedGlobal.__dockAuthOriginalFetch__ = originalFetch
  patchedGlobal.__dockAuthFetchPatched__ = true

  onServerDownChange(showServerMessage)

  // The API's live check, asked with the unpatched fetch while the API is not answering.
  const probe = async () => {
    const res = await originalFetch(`${apiBase}/health/live`, { cache: 'no-store' })
    if (isGatewayDown(res.status)) throw new TypeError('still down')
    return res
  }

  /**
   * Every call to the API goes through here (task 2447). When the API does not answer -- it is
   * restarting or unreachable -- the whole screen says so once (showServerMessage, below) and the
   * call waits: a read is sent again as soon as the API answers, so a screen never shows its own
   * "Failed to fetch". A change (POST, PUT, DELETE) is not sent again on its own, since it may
   * have reached the server: it fails with a sentence saying the server could not be reached.
   */
  const reachable = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const method = (init?.method ?? (input instanceof Request ? input.method : 'GET')).toUpperCase()
    const isRead = method === 'GET' || method === 'HEAD'
    const signal = init?.signal ?? (input instanceof Request ? input.signal : undefined)
    // Bounded, so a call the API keeps answering with a gateway error cannot loop for ever.
    for (let attempt = 0; ; attempt++) {
      await whenServerUp(signal)
      try {
        const res = await originalFetch(input, init)
        if (!isGatewayDown(res.status) || !isRead || attempt >= 20) return res
        markServerDown(probe)
      } catch (e) {
        if (!isUnanswered(e)) throw e
        markServerDown(probe)
        if (!isRead || attempt >= 20) {
          throw new Error('The server could not be reached: it is restarting or unreachable. Try again when it is back.')
        }
      }
    }
  }

  globalThis.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const requestUrl
      = typeof input === 'string'
        ? input
        : input instanceof URL
          ? input.toString()
          : input instanceof Request
            ? input.url
            : String(input)

    const isApiRequest = isApiRequestUrl(requestUrl, apiBase)
    if (!isApiRequest) {
      return originalFetch(input, init)
    }

    const token = getAccessToken()
    if (!token) {
      return reachable(input, init)
    }

    const headers = new Headers(
      init?.headers ?? (input instanceof Request ? input.headers : undefined)
    )
    if (!headers.has('Authorization')) {
      headers.set('Authorization', `Bearer ${token}`)
    }

    const response = await reachable(input, {
      ...(init ?? {}),
      credentials: init?.credentials ?? 'include',
      headers
    })
    // The one place every request passes: a log-in the server refuses is noticed
    // here and said over the screen, whichever one asked (utils/sessionExpired.ts).
    if (meansSessionExpired(pathOf(requestUrl), response.status, true)) sessionExpired.value = true
    return response
  }
})

function pathOf(requestUrl: string): string {
  try {
    return new URL(requestUrl, window.location.origin).pathname
  } catch {
    return requestUrl
  }
}

function isApiRequestUrl(requestUrl: string, apiBase: string): boolean {
  if (!apiBase) return false

  if (apiBase.startsWith('http')) {
    if (requestUrl.startsWith(apiBase)) return true

    try {
      const parsedRequest = new URL(requestUrl, window.location.origin)
      const parsedBase = new URL(apiBase)
      if (parsedRequest.origin !== parsedBase.origin) return false
      return parsedRequest.pathname.startsWith(parsedBase.pathname)
    } catch {
      return false
    }
  }

  if (apiBase.startsWith('/')) {
    try {
      const parsed = new URL(requestUrl, window.location.origin)
      return parsed.pathname.startsWith(apiBase)
    } catch {
      return requestUrl.startsWith(apiBase)
    }
  }

  return requestUrl.startsWith(apiBase)
}

/**
 * The one message while the API is restarting or unreachable (task 2447), over the whole screen in
 * the squared look (app/assets/css/dock-theme.css), with the same backdrop as a window
 * (ui/AppWindow.vue). Drawn straight into the page so it shows at start-up too, before any
 * component is; taken away when the API answers. Laid out as Arlo's offline card (Mark,
 * 2026-10-21) in BOB's theme: a coffee cup, "BOB's on a coffee break" (Mark's words), one line, and Try Again (Enter
 * presses it) beside the check that runs by itself every few seconds.
 */
function showServerMessage(isDown: boolean): void {
  const id = 'server-unreachable'
  const existing = document.getElementById(id)
  if (!isDown) {
    existing?.remove()
    return
  }
  if (existing) return
  const overlay = document.createElement('div')
  overlay.id = id
  overlay.dataset.testid = id
  overlay.className = 'dock-theme fixed inset-0 z-[1000] flex items-center justify-center bg-black/60 p-4'
  overlay.setAttribute('role', 'alertdialog')
  overlay.setAttribute('aria-modal', 'true')
  overlay.setAttribute('aria-labelledby', `${id}-title`)
  const panel = document.createElement('div')
  panel.className = 'dk-surface dk-offline flex w-full max-w-md flex-col items-center border px-7 py-8 text-center'
  // A coffee cup (lucide's coffee), where Arlo's card draws its no-signal sign.
  panel.innerHTML = '<svg class="dk-offline-sign" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M10 2v2"/><path d="M14 2v2"/><path d="M16 8a1 1 0 0 1 1 1v8a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4V9a1 1 0 0 1 1-1h14a4 4 0 1 1 0 8h-1"/><path d="M6 2v2"/></svg>'
  const title = document.createElement('h2')
  title.id = `${id}-title`
  title.className = 'dk-h dk-offline-title text-xl'
  title.textContent = 'BOB’s on a coffee break'
  const note = document.createElement('p')
  note.className = 'mt-1.5 text-sm text-foreground-muted'
  note.dataset.prose = ''
  note.textContent = 'He’ll be back in just a second. This clears by itself when he’s back, and nothing on this screen is lost.'
  const retry = document.createElement('button')
  retry.type = 'button'
  retry.className = 'dk-btn dk-btn-quiet mt-5 min-w-[8rem]'
  retry.dataset.testid = `${id}-retry`
  retry.textContent = 'Try Again'
  retry.addEventListener('click', async () => {
    if (retry.disabled) return
    retry.disabled = true
    retry.textContent = 'Checking…'
    await askServerNow()
    retry.disabled = false
    retry.textContent = 'Try Again'
  })
  panel.append(title, note, retry)
  overlay.append(panel)
  document.body.append(overlay)
  retry.focus()
}
